import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { guessPhrase } from "./labels";
import type { FindingHypothesis, FindingSeverity } from "./types";

const LEVEL: Record<FindingSeverity, number> = { low: 1, medium: 2, high: 3 };

export const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * What Volca wrote for this job: the cause description it stored, else the
 * cause it picked. Nothing invented here for the level itself.
 */
export const agentLine = (hypothesis?: FindingHypothesis | null) => {
  const description = hypothesis?.cause_description?.trim();
  if (description) return description;
  if (hypothesis?.cause) {
    return guessPhrase(
      hypothesis.cause,
      hypothesis.confidence,
      hypothesis.cause_name,
    );
  }
  return null;
};

/**
 * How much a finding matters, as three rising bars filled to its level, in
 * ink: the count is the meaning, so it borrows no status colour. Hovering
 * previews the level and Volca's reading; a click opens the row like the rest
 * of it. Not a tab stop of its own: the row's open button says the same in
 * words (`agentLine`).
 */
export const SeverityMark = ({
  severity,
  hypothesis,
  onOpen,
}: {
  severity: FindingSeverity | null;
  hypothesis?: FindingHypothesis | null;
  onOpen?: () => void;
}) => {
  const level = severity ? LEVEL[severity] : 0;
  const detail = agentLine(hypothesis);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // A pointer shortcut; the row's own button is the keyboard path.
          <span
            onClick={onOpen}
            className="pointer-events-auto inline-flex h-5 cursor-pointer items-end"
          />
        }
      >
        <span aria-hidden className="inline-flex h-3.5 items-end gap-0.5">
          {[1, 2, 3].map((bar) => (
            <span
              key={bar}
              className={cn("w-1 rounded-xs", {
                "h-1.5": bar === 1,
                "h-2.5": bar === 2,
                "h-3.5": bar === 3,
                "bg-line-strong": bar > level,
                "bg-ink": bar <= level,
              })}
            />
          ))}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-56 flex-col items-start text-start">
        <span className="font-medium">
          {severity ? SEVERITY_LABEL[severity] : "Not graded"}
        </span>
        {detail && <span>{detail}</span>}
      </TooltipContent>
    </Tooltip>
  );
};
